import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, Eye, Folder, FolderOpen, FileText, Tag, Paperclip, FileIcon, X, Download, CheckSquare, Mail } from 'lucide-react';
import ShareViaEmail from '@/components/ShareViaEmail';
import SensitivityBadge from '@/components/SensitivityBadge';
import SensitivitySelect from '@/components/SensitivitySelect';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArticlesPage = () => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('articles');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [articleTypes, setArticleTypes] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Dialog states
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isSubcategoryOpen, setIsSubcategoryOpen] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isArticleOpen, setIsArticleOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Forms
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [subcategoryForm, setSubcategoryForm] = useState({ name: '', category_id: '', description: '' });
  const [typeForm, setTypeForm] = useState({ name: '', description: '' });
  const [articleForm, setArticleForm] = useState({
    title: '', content: '', category_id: '', subcategory_id: '', type_id: '', tags: [], is_published: false,
    sensitivity: 'internal', allowed_roles: [], allowed_user_ids: []
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamFilter, setTeamFilter] = useState('');

  useEffect(() => {
    fetchAll();
    fetchTeams();
  }, [teamFilter]);

  const fetchTeams = async () => {
    try {
      const res = await axios.get(`${API}/teams`, { headers: { Authorization: `Bearer ${token}` } });
      setTeams(res.data);
    } catch (e) { /* ignore */ }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = { search };
      if (teamFilter) params.team_id = teamFilter;
      const [catRes, subRes, typeRes, artRes] = await Promise.all([
        axios.get(`${API}/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/subcategories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/article-types`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/articles`, { params, headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCategories(catRes.data);
      setSubcategories(subRes.data);
      setArticleTypes(typeRes.data);
      setArticles(artRes.data);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Category CRUD
  const handleSaveCategory = async () => {
    if (!categoryForm.name) return toast.error('Name required');
    try {
      if (editMode && selectedItem) {
        await axios.put(`${API}/categories/${selectedItem.category_id}`, categoryForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/categories`, categoryForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Category created');
      }
      setIsCategoryOpen(false);
      setCategoryForm({ name: '', description: '' });
      setEditMode(false);
      fetchAll();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!confirm('This will delete all subcategories and articles in this category. Continue?')) return;
    try {
      await axios.delete(`${API}/categories/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Category deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Subcategory CRUD
  const handleSaveSubcategory = async () => {
    if (!subcategoryForm.name || !subcategoryForm.category_id) return toast.error('Name and category required');
    try {
      if (editMode && selectedItem) {
        await axios.put(`${API}/subcategories/${selectedItem.subcategory_id}`, subcategoryForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Subcategory updated');
      } else {
        await axios.post(`${API}/subcategories`, subcategoryForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Subcategory created');
      }
      setIsSubcategoryOpen(false);
      setSubcategoryForm({ name: '', category_id: '', description: '' });
      setEditMode(false);
      fetchAll();
    } catch (error) {
      toast.error('Failed to save subcategory');
    }
  };

  const handleDeleteSubcategory = async (id) => {
    if (!confirm('Delete this subcategory?')) return;
    try {
      await axios.delete(`${API}/subcategories/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Subcategory deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Article Type CRUD
  const handleSaveType = async () => {
    if (!typeForm.name) return toast.error('Name required');
    try {
      if (editMode && selectedItem) {
        await axios.put(`${API}/article-types/${selectedItem.type_id}`, typeForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Type updated');
      } else {
        await axios.post(`${API}/article-types`, typeForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Type created');
      }
      setIsTypeOpen(false);
      setTypeForm({ name: '', description: '' });
      setEditMode(false);
      fetchAll();
    } catch (error) {
      toast.error('Failed to save type');
    }
  };

  const handleDeleteType = async (id) => {
    if (!confirm('Delete this type?')) return;
    try {
      await axios.delete(`${API}/article-types/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Type deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Article CRUD
  const handleSaveArticle = async () => {
    if (!articleForm.title || !articleForm.content || !articleForm.category_id) {
      return toast.error('Title, content and category required');
    }
    try {
      if (editMode && selectedItem) {
        await axios.put(`${API}/articles/${selectedItem.article_id}`, articleForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/articles`, articleForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Article created');
      }
      setIsArticleOpen(false);
      setArticleForm({ title: '', content: '', category_id: '', subcategory_id: '', type_id: '', tags: [], is_published: false, sensitivity: 'internal', allowed_roles: [], allowed_user_ids: [] });
      setEditMode(false);
      fetchAll();
    } catch (error) {
      toast.error('Failed to save article');
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API}/articles/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Article deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleBulkDeleteArticles = async () => {
    if (!selectedIds.length) return;
    if (!confirm(`Delete ${selectedIds.length} article(s)?`)) return;
    try {
      await axios.post(`${API}/articles/bulk/delete`, { article_ids: selectedIds }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${selectedIds.length} articles deleted`);
      setSelectedIds([]);
      fetchAll();
    } catch (err) { toast.error('Bulk delete failed'); }
  };

  const handleBulkPublish = async (publish) => {
    if (!selectedIds.length) return;
    try {
      await axios.post(`${API}/articles/bulk/publish`, { article_ids: selectedIds, publish }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${selectedIds.length} articles ${publish ? 'published' : 'unpublished'}`);
      setSelectedIds([]);
      fetchAll();
    } catch (err) { toast.error('Bulk update failed'); }
  };

  const handleExportArticles = async (format) => {
    const params = new URLSearchParams({ format });
    if (teamFilter) params.set('team_id', teamFilter);
    try {
      const res = await axios.get(`${API}/export/articles?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const mimeTypes = { pdf: 'application/pdf', csv: 'text/csv', json: 'application/json' };
      const blob = new Blob([res.data], { type: mimeTypes[format] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `articles-export.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const toggleArticleSelect = (articleId) => {
    setSelectedIds(prev => prev.includes(articleId) ? prev.filter(id => id !== articleId) : [...prev, articleId]);
  };

  const toggleSelectAllArticles = () => {
    if (selectedIds.length === articles.length) setSelectedIds([]);
    else setSelectedIds(articles.map(a => a.article_id));
  };

  const handleAttachToArticle = (articleId) => {
    fileInputRef.current?.click();
    fileInputRef.current.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast.error('File too large. Max 10MB.'); return; }
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axios.post(`${API}/articles/${articleId}/attachments`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        toast.success('File attached');
        fetchAll();
        if (selectedItem?.article_id === articleId) {
          const res = await axios.get(`${API}/articles/${articleId}`, { headers: { Authorization: `Bearer ${token}` } });
          setSelectedItem(res.data);
        }
      } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
      finally { e.target.value = ''; }
    };
  };

  const handleRemoveArticleAttachment = async (articleId, fileId) => {
    try {
      await axios.delete(`${API}/articles/${articleId}/attachments/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Attachment removed');
      fetchAll();
      if (selectedItem?.article_id === articleId) {
        setSelectedItem(prev => ({ ...prev, attachments: prev.attachments.filter(a => a.file_id !== fileId) }));
      }
    } catch (err) { toast.error('Failed to remove'); }
  };

  const openEditCategory = (cat) => {
    setSelectedItem(cat);
    setCategoryForm({ name: cat.name, description: cat.description || '' });
    setEditMode(true);
    setIsCategoryOpen(true);
  };

  const openEditSubcategory = (sub) => {
    setSelectedItem(sub);
    setSubcategoryForm({ name: sub.name, category_id: sub.category_id, description: sub.description || '' });
    setEditMode(true);
    setIsSubcategoryOpen(true);
  };

  const openEditType = (type) => {
    setSelectedItem(type);
    setTypeForm({ name: type.name, description: type.description || '' });
    setEditMode(true);
    setIsTypeOpen(true);
  };

  const openEditArticle = (article) => {
    setSelectedItem(article);
    setArticleForm({
      title: article.title, content: article.content, category_id: article.category_id,
      subcategory_id: article.subcategory_id || '', type_id: article.type_id || '',
      tags: article.tags || [], is_published: article.is_published,
      sensitivity: article.sensitivity || 'internal',
      allowed_roles: article.allowed_roles || [],
      allowed_user_ids: article.allowed_user_ids || []
    });
    setEditMode(true);
    setIsArticleOpen(true);
  };

  const getCategoryName = (id) => categories.find(c => c.category_id === id)?.name || '-';

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  return (
    <div data-testid="articles-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Articles</h1>
          <p className="text-[#4B5563] text-sm">Manage categories, types, and articles</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="articles" data-testid="articles-tab">Articles</TabsTrigger>
          <TabsTrigger value="categories" data-testid="categories-tab">Categories</TabsTrigger>
          <TabsTrigger value="subcategories" data-testid="subcategories-tab">Subcategories</TabsTrigger>
          <TabsTrigger value="types" data-testid="types-tab">Types</TabsTrigger>
        </TabsList>

        {/* Articles Tab */}
        <TabsContent value="articles">
          <Card className="border-[#E5E7EB] mb-4">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative max-w-md flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <Input placeholder="Search articles..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="article-search" />
                </div>
                <Select value={teamFilter || "all"} onValueChange={(v) => { setTeamFilter(v === "all" ? "" : v); }}>
                  <SelectTrigger className="w-[140px]" data-testid="article-team-filter"><SelectValue placeholder="Team" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teams</SelectItem>
                    {teams.map(t => <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => handleExportArticles('csv')} data-testid="export-articles-csv">
                    <Download className="w-3.5 h-3.5 mr-1" />CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExportArticles('txt')} data-testid="export-articles-txt">
                    <Download className="w-3.5 h-3.5 mr-1" />TXT
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExportArticles('pdf')} data-testid="export-articles-pdf">
                    <Download className="w-3.5 h-3.5 mr-1" />PDF
                  </Button>
                </div>
                <Button className="bg-[#002FA7] hover:bg-[#00227A] ml-auto" onClick={() => { setEditMode(false); setArticleForm({ title: '', content: '', category_id: '', subcategory_id: '', type_id: '', tags: [], is_published: false, sensitivity: 'internal', allowed_roles: [], allowed_user_ids: [] }); setIsArticleOpen(true); }} data-testid="create-article-btn">
                  <Plus className="w-4 h-4 mr-2" />New Article
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Actions */}
          {selectedIds.length > 0 && (
            <Card className="border-[#002FA7] bg-[#002FA7]/5 mb-4" data-testid="article-bulk-bar">
              <CardContent className="p-3 flex items-center gap-3">
                <span className="text-sm font-medium">{selectedIds.length} selected</span>
                <div className="flex gap-2 ml-auto">
                  <Button size="sm" variant="outline" onClick={() => handleBulkPublish(true)} data-testid="bulk-publish-btn">
                    <CheckSquare className="w-3.5 h-3.5 mr-1" />Publish
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleBulkPublish(false)} data-testid="bulk-unpublish-btn">
                    Unpublish
                  </Button>
                  <Button size="sm" variant="outline" className="text-[#FF3B30] border-[#FF3B30]" onClick={handleBulkDeleteArticles} data-testid="bulk-delete-articles-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-[#E5E7EB]">
            <CardContent className="p-0">
              <Table data-testid="articles-table">
                <TableHeader><TableRow className="bg-[#F3F4F6]">
                  <TableHead className="w-10"><input type="checkbox" checked={selectedIds.length === articles.length && articles.length > 0} onChange={toggleSelectAllArticles} className="rounded border-[#D1D5DB]" /></TableHead>
                  <TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Status</TableHead><TableHead>Views</TableHead><TableHead>Files</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {articles.filter(a => !search || a.title.toLowerCase().includes(search.toLowerCase())).map(article => (
                    <TableRow key={article.article_id} className={selectedIds.includes(article.article_id) ? 'bg-[#002FA7]/5' : ''}>
                      <TableCell><input type="checkbox" checked={selectedIds.includes(article.article_id)} onChange={() => toggleArticleSelect(article.article_id)} className="rounded border-[#D1D5DB]" /></TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{article.title}</span>
                          {article.sensitivity && article.sensitivity !== 'internal' && (
                            <SensitivityBadge level={article.sensitivity} size="sm" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getCategoryName(article.category_id)}</TableCell>
                      <TableCell><Badge className={article.is_published ? 'bg-[#34C759] text-white' : 'bg-[#E5E7EB] text-[#4B5563]'}>{article.is_published ? 'Published' : 'Draft'}</Badge></TableCell>
                      <TableCell>{article.views || 0}</TableCell>
                      <TableCell>{article.attachments?.length || 0}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => { setSelectedItem(article); setIsViewOpen(true); }}><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditArticle(article)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-[#FF3B30]" onClick={() => handleDeleteArticle(article.article_id)}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card className="border-[#E5E7EB] mb-4">
            <CardContent className="p-4 flex justify-end">
              <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => { setEditMode(false); setCategoryForm({ name: '', description: '' }); setIsCategoryOpen(true); }} data-testid="create-category-btn">
                <Plus className="w-4 h-4 mr-2" />New Category
              </Button>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map(cat => (
              <Card key={cat.category_id} className="border-[#E5E7EB] card-hover">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center"><Folder className="w-5 h-5 text-[#002FA7]" /></div>
                      <div><h3 className="font-semibold">{cat.name}</h3><p className="text-sm text-[#9CA3AF]">{subcategories.filter(s => s.category_id === cat.category_id).length} subcategories</p></div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditCategory(cat)}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" className="text-[#FF3B30]" onClick={() => handleDeleteCategory(cat.category_id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Subcategories Tab */}
        <TabsContent value="subcategories">
          <Card className="border-[#E5E7EB] mb-4">
            <CardContent className="p-4 flex justify-end">
              <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => { setEditMode(false); setSubcategoryForm({ name: '', category_id: '', description: '' }); setIsSubcategoryOpen(true); }} data-testid="create-subcategory-btn">
                <Plus className="w-4 h-4 mr-2" />New Subcategory
              </Button>
            </CardContent>
          </Card>
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-[#F3F4F6]"><TableHead>Name</TableHead><TableHead>Parent Category</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {subcategories.map(sub => (
                    <TableRow key={sub.subcategory_id}>
                      <TableCell className="font-medium">{sub.name}</TableCell>
                      <TableCell>{getCategoryName(sub.category_id)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEditSubcategory(sub)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-[#FF3B30]" onClick={() => handleDeleteSubcategory(sub.subcategory_id)}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Types Tab */}
        <TabsContent value="types">
          <Card className="border-[#E5E7EB] mb-4">
            <CardContent className="p-4 flex justify-end">
              <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => { setEditMode(false); setTypeForm({ name: '', description: '' }); setIsTypeOpen(true); }} data-testid="create-type-btn">
                <Plus className="w-4 h-4 mr-2" />New Type
              </Button>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {articleTypes.map(type => (
              <Card key={type.type_id} className="border-[#E5E7EB] card-hover">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#FF9500]/10 flex items-center justify-center"><Tag className="w-5 h-5 text-[#FF9500]" /></div>
                      <div><h3 className="font-semibold">{type.name}</h3><p className="text-sm text-[#9CA3AF]">{type.description || 'No description'}</p></div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditType(type)}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" className="text-[#FF3B30]" onClick={() => handleDeleteType(type.type_id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={isCategoryOpen} onOpenChange={setIsCategoryOpen}>
        <DialogContent><DialogHeader><DialogTitle>{editMode ? 'Edit' : 'New'} Category</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} data-testid="category-name-input" /></div>
            <div><Label>Description</Label><Textarea value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setIsCategoryOpen(false)}>Cancel</Button><Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleSaveCategory} data-testid="save-category-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subcategory Dialog */}
      <Dialog open={isSubcategoryOpen} onOpenChange={setIsSubcategoryOpen}>
        <DialogContent><DialogHeader><DialogTitle>{editMode ? 'Edit' : 'New'} Subcategory</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={subcategoryForm.name} onChange={(e) => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })} /></div>
            <div><Label>Parent Category *</Label>
              <Select value={subcategoryForm.category_id} onValueChange={(v) => setSubcategoryForm({ ...subcategoryForm, category_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={subcategoryForm.description} onChange={(e) => setSubcategoryForm({ ...subcategoryForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setIsSubcategoryOpen(false)}>Cancel</Button><Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleSaveSubcategory}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Type Dialog */}
      <Dialog open={isTypeOpen} onOpenChange={setIsTypeOpen}>
        <DialogContent><DialogHeader><DialogTitle>{editMode ? 'Edit' : 'New'} Article Type</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea value={typeForm.description} onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setIsTypeOpen(false)}>Cancel</Button><Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleSaveType}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Article Dialog */}
      <Dialog open={isArticleOpen} onOpenChange={setIsArticleOpen}>
        <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>{editMode ? 'Edit' : 'New'} Article</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div><Label>Title *</Label><Input value={articleForm.title} onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })} data-testid="article-title-input" /></div>
            <div><Label>Content *</Label><Textarea rows={6} value={articleForm.content} onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })} data-testid="article-content-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Category *</Label>
                <Select value={articleForm.category_id} onValueChange={(v) => setArticleForm({ ...articleForm, category_id: v })}>
                  <SelectTrigger data-testid="article-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Subcategory</Label>
                <Select value={articleForm.subcategory_id} onValueChange={(v) => setArticleForm({ ...articleForm, subcategory_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select subcategory" /></SelectTrigger>
                  <SelectContent>{subcategories.filter(s => s.category_id === articleForm.category_id).map(s => <SelectItem key={s.subcategory_id} value={s.subcategory_id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Type</Label>
                <Select value={articleForm.type_id} onValueChange={(v) => setArticleForm({ ...articleForm, type_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>{articleTypes.map(t => <SelectItem key={t.type_id} value={t.type_id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={articleForm.is_published} onChange={(e) => setArticleForm({ ...articleForm, is_published: e.target.checked })} className="w-4 h-4" />
                  <span>Publish immediately</span>
                </label>
              </div>
            </div>
            <SensitivitySelect
              value={articleForm.sensitivity}
              onChange={(v) => setArticleForm({ ...articleForm, sensitivity: v })}
            />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setIsArticleOpen(false)}>Cancel</Button><Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleSaveArticle} data-testid="save-article-btn">Save Article</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden file input */}
      <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx,.txt,.md,.csv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.zip" />

      {/* View Article Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Article Details</DialogTitle></DialogHeader>
          {selectedItem && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="flex items-start justify-between">
                <h2 className="text-xl font-semibold">{selectedItem.title}</h2>
                <Badge className={selectedItem.is_published ? 'bg-[#34C759] text-white' : 'bg-[#E5E7EB] text-[#4B5563]'}>{selectedItem.is_published ? 'Published' : 'Draft'}</Badge>
              </div>
              <div className="text-sm text-[#9CA3AF]">Category: {getCategoryName(selectedItem.category_id)} | Views: {selectedItem.views || 0}</div>
              <div className="prose prose-sm max-w-none"><p className="whitespace-pre-wrap">{selectedItem.content}</p></div>
              {selectedItem.tags?.length > 0 && (
                <div className="flex gap-2 flex-wrap">{selectedItem.tags.map((tag, i) => <Badge key={i} variant="secondary">{tag}</Badge>)}</div>
              )}
              {/* Attachments */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-sm">Attachments ({selectedItem.attachments?.length || 0})</h4>
                  <Button variant="outline" size="sm" onClick={() => handleAttachToArticle(selectedItem.article_id)} data-testid="article-attach-btn">
                    <Paperclip className="w-3.5 h-3.5 mr-1" />Attach File
                  </Button>
                </div>
                {selectedItem.attachments?.length > 0 && (
                  <div className="space-y-1.5">
                    {selectedItem.attachments.map((att) => (
                      <div key={att.file_id} className="flex items-center justify-between p-2 bg-[#F3F4F6] rounded text-sm" data-testid={`article-att-${att.file_id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <FileIcon className="w-4 h-4 text-[#002FA7] flex-shrink-0" />
                          <span className="truncate">{att.original_filename}</span>
                          <span className="text-xs text-[#9CA3AF] flex-shrink-0">{(att.size / 1024).toFixed(0)}KB</span>
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[#FF3B30]" onClick={() => handleRemoveArticleAttachment(selectedItem.article_id, att.file_id)}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end pt-3 border-t border-[#E5E7EB]">
                <Button variant="outline" onClick={() => setShareOpen(true)} data-testid="share-article-btn">
                  <Mail className="w-4 h-4 mr-2" /> Share via Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {selectedItem && (
        <ShareViaEmail
          open={shareOpen}
          onOpenChange={setShareOpen}
          subject={`Article: ${selectedItem.title}`}
          content={selectedItem.content}
          contentType="Article"
        />
      )}
    </div>
  );
};

export default ArticlesPage;
